import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import loadAuth from '../apis/auth';
import { clearImpersonationSession, getImpersonationSession } from '../helpers/impersonation';

const ImpersonationBanner = () => {
    const { profile, user } = useContext(UserContext);
    const [active, setActive] = useState(false);
    const [returning, setReturning] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setActive(!!getImpersonationSession());
    }, []);

    const returnToAdmin = async () => {
        setReturning(true);
        try {
            const auth = await loadAuth();
            await auth.signOut();
            clearImpersonationSession();
            try {
                if (window.opener && !window.opener.closed) {
                    window.opener.focus();
                    window.close();
                }
            } catch (error) {
                // The original tab may have navigated to a different origin.
            }
            // Also works if the original tab was closed or closing is blocked.
            window.location.replace('/manage/users/');
        } catch (error) {
            setError('Unable to end this session. You can close this tab to return to admin.');
            setReturning(false);
        }
    };

    if (!active) return null;

    return (
        <aside aria-label="Impersonation session">
            <div className="session-info">
                <span className="session-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
                    </svg>
                </span>
                <strong title={profile?.name || user?.email || 'User view'}>
                    {user ? `Viewing as ${profile?.name || user.email || 'user'}` : 'User view'}
                </strong>
            </div>
            <button
                type="button"
                onClick={returnToAdmin}
                disabled={returning}
                aria-label="End session & return to admin"
                title="End this user session and return to admin"
            >
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                        d="m10 6-6 6 6 6M4 12h16"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                {returning ? 'Returning…' : 'Return to admin'}
            </button>
            {error && (
                <span className="session-error" role="alert">
                    {error}
                </span>
            )}
            <style jsx>{`
                aside {
                    position: fixed;
                    bottom: 16px;
                    left: 16px;
                    right: 16px;
                    max-width: 640px;
                    margin: 0 auto;
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 12px;
                    padding: 8px 10px 8px 12px;
                    background: linear-gradient(115deg, #b52d25, #d6382e);
                    color: #fff;
                    border: 1px solid rgba(255, 193, 186, 0.4);
                    border-radius: 14px;
                    box-shadow: 0 8px 28px rgba(115, 28, 23, 0.25);
                }
                .session-info {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    flex: 1;
                    min-width: 0;
                }
                .session-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 30px;
                    height: 30px;
                    border-radius: 9px;
                    color: #fff;
                    background: rgba(255, 255, 255, 0.12);
                }
                strong {
                    font-size: 13px;
                    font-weight: 500;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }
                button {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    padding: 8px 10px;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    border-radius: 8px;
                    color: #fff;
                    background: rgba(255, 255, 255, 0.12);
                    flex-shrink: 0;
                    font-size: 12px;
                    font-weight: 500;
                    transition: background 0.15s, border-color 0.15s;
                }
                button:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.22);
                    border-color: rgba(255, 255, 255, 0.55);
                }
                button:focus-visible {
                    outline: 2px solid #fff;
                    outline-offset: 3px;
                }
                button:disabled {
                    cursor: wait;
                    opacity: 0.6;
                }
                :global(body) {
                    padding-bottom: 80px;
                }
                .session-error {
                    flex-basis: 100%;
                    font-size: 12px;
                    line-height: 1.5;
                    color: #fff;
                }
                @media (max-width: 699px) {
                    aside {
                        bottom: 91px;
                    }
                    :global(body) {
                        padding-bottom: 155px;
                    }
                }
                @media (max-width: 600px) {
                    aside {
                        bottom: 85px;
                        left: 10px;
                        right: 10px;
                        gap: 8px;
                        padding: 7px 8px;
                        border-radius: 12px;
                    }
                    .session-info {
                        gap: 7px;
                    }
                    .session-icon {
                        width: 26px;
                        height: 26px;
                    }
                    strong {
                        font-size: 12px;
                    }
                    button {
                        padding: 8px;
                    }
                }
            `}</style>
        </aside>
    );
};

export default ImpersonationBanner;
